// ./src/components/features/Events/eventListHelpers.ts
//
// Client-side splitting/sorting/search for the All Events page (the API has no
// date-range filter yet). "Upcoming" holds events that are still to come OR
// currently ongoing — so a recurring / multi-day event stays upcoming until its
// end passes ("as soon as it lasts"). Undated drafts are treated as upcoming.

import dayjs from "dayjs";
import { TFunction } from "i18next";

import { formatDateTime } from "@em/utils";
import { Event } from "@/utils/api";

import { GUEST_MODE, GUEST_MODES } from "./controls/guestModeConfig";

/**
 * The moment an event stops being "upcoming": a RECURRING event's LAST date (`seriesEndsAt` — its own `endsAt` is only its
 * first date's, which filed Tech Tuesdays under Past with Tuesdays still to come; owner, 2026-09-14), else its end, else its
 * start.
 */
const effectiveEnd = (event: Event): string | null => event.seriesEndsAt || event.endsAt || event.startsAt || null;

/** When an upcoming event is next on: a recurring event's next date, else its start. */
const nextOn = (event: Event): string | null => event.nextDateStartsAt || event.startsAt || null;

/** Upcoming = not yet ended (includes ongoing) or undated. */
export const isUpcomingEvent = (event: Event, now = dayjs()): boolean => {
  const end = effectiveEnd(event);
  if (!end) {
    return true; // no date yet — it hasn't happened
  }
  const endDay = dayjs(end);
  return endDay.isValid() ? !endDay.isBefore(now) : true;
};

/** Case-insensitive match on title + subtitle. */
export const matchesSearch = (event: Event, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) {
    return true;
  }
  return (
    (event.title ?? "").toLowerCase().includes(q) || (event.subtitle ?? "").toLowerCase().includes(q)
  );
};

/** Soonest-first by when each is next on (a recurring event by its next date); undated (being planned) float to the top. */
const byStartAsc = (a: Event, b: Event): number => {
  const an = nextOn(a);
  const bn = nextOn(b);
  if (!an && !bn) return 0;
  if (!an) return -1;
  if (!bn) return 1;
  return dayjs(an).valueOf() - dayjs(bn).valueOf();
};

/** Most-recent-first for past events. */
const byStartDesc = (a: Event, b: Event): number => {
  if (!a.startsAt && !b.startsAt) return 0;
  if (!a.startsAt) return 1;
  if (!b.startsAt) return -1;
  return dayjs(b.startsAt).valueOf() - dayjs(a.startsAt).valueOf();
};

/**
 * The lifecycle badge for an event, in ONE place.
 *
 * ⚠ There were two copies of this (the All Events card and the event dashboard) when Home needed a
 * third — which is precisely the point at which a vocabulary starts drifting: a colour changed in
 * one place and a host sees the same event badged differently on two screens.
 */
export const EVENT_STATUS_META: Record<string, { color: string; label: string }> = {
  draft: { color: "gray", label: "Draft" },
  published: { color: "emerald", label: "Published" },
  canceled: { color: "red", label: "Canceled" },
  completed: { color: "blue", label: "Completed" },
};

/** The badge for an event's status, falling back to Draft for an unknown value. */
export const eventStatusMeta = (eventStatus?: string | null) =>
  EVENT_STATUS_META[eventStatus ?? "draft"] ?? EVENT_STATUS_META.draft;

export interface SplitEvents {
  upcoming: Event[];
  past: Event[];
}

/** Split the account's events into upcoming/past, searched + sorted per tab. */
export const splitEvents = (events: Event[], search: string): SplitEvents => {
  const now = dayjs();
  const matched = events.filter((event) => matchesSearch(event, search));
  return {
    upcoming: matched.filter((event) => isUpcomingEvent(event, now)).sort(byStartAsc),
    past: matched.filter((event) => !isUpcomingEvent(event, now)).sort(byStartDesc),
  };
};

/**
 * Whether the server granted `remove` on this event (2026-10-04 — an unpublished one only). ⚠ The right must be THERE —
 * not `recordHasRight`, which reads no rights as "allowed": an API that sends none (deployed before this) would offer
 * Remove on a published event and remove it.
 */
export const eventRemovable = (event: { rights?: string[] | null }): boolean => Array.isArray(event.rights) && event.rights.includes('remove');

/** How an event happens, for the list (card + table). An unknown value is shown as stored. */
export const EVENT_FORMAT_LABELS: Record<string, string> = {
  in_person: 'In person',
  online: 'Online',
  hybrid: 'Hybrid',
};

/** The schedule badge for a non-single event. */
export const EVENT_SCHEDULE_BADGES: Record<string, string> = {
  recurring: 'Repeats',
  agenda: 'Multi-day',
};

/** The event's format label, or null when none is set. */
export const eventFormatLabel = (event: Event): string | null => (event.eventFormat ? EVENT_FORMAT_LABELS[event.eventFormat] ?? event.eventFormat : null);

/**
 * When the event is on, as the list shows it — ONE wording for the card and the table.
 * ROADMAP A4 — a recurring event's own start is its FIRST date, long gone for a series under way: show the next one.
 * ⚠ In the EVENT's time zone (2026-10-04) — the one its dates are entered and shown in, never this device's.
 */
export const eventWhenText = (event: Event, t: TFunction): string => {
  const tz = event.timezone || undefined;
  const style = event.isAllDay ? 'long' : 'datetime';

  if (event.nextDateStartsAt) {
    return t('events:list.nextDate', { defaultValue: 'Next: {{when}}', when: formatDateTime(event.nextDateStartsAt, style, tz) });
  }

  if (event.startsAt) {
    return formatDateTime(event.startsAt, style, tz);
  }

  return t('events:list.dateTbd', { defaultValue: 'Date TBD' });
};

/**
 * How guests join an event, as the LISTS show it (2026-10-07 — owner: "in the event list / crew events it is unclear what
 * event is this - save the date, open rsvp, ticketed, invite"). The label is the one the event's own "How guests join"
 * control uses (`GUEST_MODES`), so the list and the editor never name a mode two ways; the icon is the list's.
 */
const EVENT_MODE_ICONS: Record<string, string> = {
  [GUEST_MODE.SAVE_THE_DATE]: 'CalendarTime',
  [GUEST_MODE.MANAGED_INVITES]: 'Mail',
  [GUEST_MODE.LINK_RSVP]: 'Link',
  [GUEST_MODE.TICKETED]: 'Ticket',
};

/** The mode's value, label and icon — null for an absent or unknown mode (the list then shows nothing rather than guess). */
export const eventModeMeta = (eventMode?: string | null): { value: string; label: string; icon: string } | null => {
  const mode = GUEST_MODES.find((m) => m.value === eventMode);

  if (!mode) {
    return null;
  }

  return { value: mode.value, label: mode.label, icon: EVENT_MODE_ICONS[mode.value] };
};
